/* eslint-disable no-param-reassign */

import { OMT_AdPlacementService, AD_TYPES } from './OMT_AdPlacementService';
import { OMT_Utils } from '../utils/OMT_Utils';
import { OMT_CrossPromoVideoPlayer } from '../OMT_CrossPromoVideoPlayer';
import { XPROMO_PLACEMENTS } from '../OMT_CrossPromo';

// for ad fallbacks
export const ASK_FRIENDS_REQUIRED_FRIENDS = 3;
export const ASK_FRIENDS_COOLDOWN_ID = 'adFallback1';

/**
 * class for handling ad display logic and interfacing with the game and the OMT_AdsPlacement service
 */
export class OMT_Ads {
    /**
     * constructor
     */
    constructor() {
        // param(s) for last showAd() values
        this._lastAdPlacement = null;

        // for debugging ad fallbacks
        this._forceAdFail = false;

        // define phaser signals
        this.signals = {
            onAdSuccess: new Phaser.Signal(),
        };
    }

    /**
     * initialize placement service
     */
    init() {
        if (G.BuildEnvironment.adPlacementIDs) {
            const preloadAds = !G.firstTime;
            this._placementService = new OMT_AdPlacementService(preloadAds);
        } else {
            OMT_Utils.stylizedLog('OMT_Ads: G.BuildEnvironment.adPlacementID was not defined so OMT_AdPlacementService could not be initialized', '#FFFF00');
        }
    }

    /**
     * Show an advertisement. Intefaces with the AdPlacementService.
     * @param {Object} placement placement Object
     * @param {Function} successCallback
     * @param {Function} failCallback
     */
    showAd(placement, successCallback, failCallback) {
        this._lastAdPlacement = placement;

        DDNA.adEventHelper.trackAdRequest(placement);

        if (this._forceAdFail) { // we want to force ad failure for testing purposes
            failCallback();
            DDNA.adEventHelper.trackAdShown(placement, null, false);
            return;
        }

        const onAdSuccess = (trackAdShown = true) => { // on ad show success
            if (trackAdShown) DDNA.adEventHelper.trackAdShown(placement, null, true);
            if (this.adOverlay) this.adOverlay.destroy();
            G.adAndVisibilityCheck(() => OMT.ads._resumeGame());
            this.signals.onAdSuccess.dispatch(placement);
            successCallback();
        };

        const onAdFail = () => { // on ad show failure
            OMT.platformTracking.logEvent(OMT.platformTracking.Events.AdFillrateError);
            DDNA.adEventHelper.trackAdShown(placement, null, false);
            // show alternate cross promo video if available in place of rewarded ads
            if (placement.adType === AD_TYPES.REWARDED && OMT.crossPromo.isPlacementActive(XPROMO_PLACEMENTS.NO_REWARDED_ADS)) {
                const promo = OMT.crossPromo.getPromoByPlacementId(XPROMO_PLACEMENTS.NO_REWARDED_ADS);
                const onPromoSuccess = () => { onAdSuccess(false); };
                const onPromoFail = () => {
                    if (this.adOverlay) this.adOverlay.destroy();
                    G.adAndVisibilityCheck(() => OMT.ads._resumeGame());
                    failCallback();
                };
                OMT_CrossPromoVideoPlayer.getInstance().playVideoAd(XPROMO_PLACEMENTS.NO_REWARDED_ADS, promo, onPromoSuccess, onPromoFail);
            } else { // no promo fallback
                if (this.adOverlay) this.adOverlay.destroy();
                G.adAndVisibilityCheck(() => OMT.ads._resumeGame());
                failCallback();
            }
        };

        if (!G.BuildEnvironment.production) {
            this.adOverlay = new G.AdLoadOverlay();
            this.mockAds(placement, onAdSuccess, onAdFail);
            return;
        }

        if (placement == null) {
            console.warn('call to adShow with no placement => skipped');
            return;
        }

        // attempt to show an ad
        if (G.BuildEnvironment.adPlacementIDs) {
            OMT.ads._pauseGame();
            this.adOverlay = new G.AdLoadOverlay();
            this._placementService.showAd(placement, onAdSuccess, onAdFail);
        } else { // placementIDs not defined so we cant show ads
            onAdFail();
        }
    }

    /**
     * show a mock ad
     * @param {Object} placement Object
     * @param {Function} successCallback
     * @param {Function} failCallback
     */
    mockAds(placement, successCallback, failCallback) {
        if (Math.random() < 0.2) {
            failCallback();
        } else {
            successCallback();
            this.signals.onAdSuccess.dispatch(placement);
        }
    }

    /**
     * show an ad-fallback
     * @param {Function} onSuccess
     * @param {string} layerName
     * @param {number} retryCount this should only be passed interally, leave unset.
     */
    async showAdFallback(onSuccess, layerName = null, retryCount = 0) {
        if (!layerName) layerName = G.WindowMgr.LayerNames.OverlayLayer;
        const askFriendsAvailable = await this.checkIfAskFriendsAvailable();
        const onRetry = retryCount === 0 ? this._adFallbackRetryLastAd.bind(this, onSuccess, layerName, retryCount) : null;

        if (askFriendsAvailable) {
            G.sb('pushWindow').dispatch(['FallbackAskFriends', onSuccess, onRetry], null, layerName, true);
        } else {
            G.sb('pushWindow').dispatch(['NoAdsOpenCoinShop', onRetry], null, layerName, true);
        }
    }

    /**
     * retries the last showAd() call for ad-fallback retry
     * @param {Function} onSuccess
     * @param {string} layerName
     * @param {number} retryCount
     */
    _adFallbackRetryLastAd(onSuccess, layerName, retryCount) {
        this.showAd(this._lastAdPlacement, onSuccess, () => {
            this.showAdFallback(onSuccess, layerName, retryCount + 1);
        });
    }

    /**
     * check if the ask friends functionality is available
     */
    async checkIfAskFriendsAvailable() {
        const requiredFriends = ASK_FRIENDS_REQUIRED_FRIENDS - G.saveState.adFallback_askFriends_getAskCount();
        console.log("ads");
        const friendList = await OMT.friends.getFriendsList();

        if (friendList.length < requiredFriends) return true;

        let friendsAvailable = 0;
        let cooldownRemaining;
        friendList.forEach((friendData) => {
            cooldownRemaining = G.saveState.getUserCooldownRemaining(ASK_FRIENDS_COOLDOWN_ID, friendData.userId);
            if (cooldownRemaining === 0) friendsAvailable++;
        });

        return friendsAvailable >= requiredFriends;
    }

    /**
     * try to resume sound context if lost
     */
    _resumeGame() {
        game.paused = false;
        G.soundManager.resume(true); // resume sound after ads

        setTimeout(() => {
            try {
                if (game.sound.context.state !== 'running') {
                    game.sound.context.resume()
                        .then(() => {
                            // console.log('ac resumed');
                        })
                        .catch(() => {
                            // console.log('ac not resumed');
                        });
                }
            } catch (e) {
                console.log(e);
            }
        }, 1000);
    }

    _pauseGame() {
        G.soundManager.pause(true); // pause sound during ads
    }

    /**
     * set to true to force ad fail to trigger fallback
     * @param {boolean} state
     */
    set forceAdFail(state) {
        this._forceAdFail = state;
    }
}